import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import AlertMarcarDesconto from "../../AlertMarcarDesconto";
import Recebimento from "./Recebimento";
import { IInputRegRecebimento } from "../../../../../../../../../types/mutations/financeiro/registrar-recebimento-titulos";
import axiosClient from "../../../../../../../../../lib/axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../../../../../../../helpers/masks";
import BtnActionWrapper from "../../../../../../BtnActionWrapper";
import { TObjValueLoja } from "../../../../../../../../../components/SelectsSimpleApiQuery/SelectLoja";
import { IInternForm } from "./Recebimento/DialogCadastrarRecb";
import { desformatarCPFCNPJ } from "../../../../../../../../../helpers/unmask";
import { TOperadoraCartao } from "../../../../../../../../../types/api/financeiro/operadoras-cartao";
import { useMemo } from "react";
import { IDataListaTitulosReceber } from "../../../../../../../../../types/api/financeiro/pesquisar-titulos-receber";
import AlertMarcarJuros from "../../AlertMarcarJuros";
import SelectLojaComPesquisa, { TLoja } from "../../../../../../../../../components/SelectsSimpleApiQuery/SelectLojaComPesquisa";
import { usePermission } from "../../../../../../../../../components/PermissionGuard";
import AplicarCupomDesconto from "../../AplicarCupomDesconto";

export default function RegistrarRecebimento() {
  const queryClient = useQueryClient();

  const { getValues, watch, setValue, handleSubmit, control } =
    useFormContext();

  //valores importantes
  const activeStep = watch("stepper");
  const ignorarGravacaoCaixaBancos = usePermission('financeiro.contas-receber-ignorar-gravacao-caixa-bancos')

  const lojaSelect = watch("finalizacao.loja");
  const titulos = getValues("selecionar_titulos");

  const verificarRegistrarBtn = () => {
    const ultimoStep = activeStep === 2;
    const lojaRegistrada = Boolean(watch("finalizacao.loja"));
    const valorRegistrado = watch("finalizacao.recebimentos").length > 0;

    // Verificação de valor total
    const totalValor = finalizacao.recebimentos.reduce(
      (acumulador: number, item: any) =>
        acumulador + formatarMonetárioParaNúmero(item.valor),
      0
    );
    const formatValorTotal = Math.round(totalValor * 100) / 100;
    const valorExcedido = formatValorTotal > total_atualizado && !finalizacao.aplicarDiferencaComoJuros;

    return !(ultimoStep && valorRegistrado && lojaRegistrada) || valorExcedido;
  };
  //

  //queries e mutations
  const { data } = useQuery({
    queryKey: ["pesquisar-operadoras-cartao", titulos],
    queryFn: () =>
      axiosClient.get("financeiro/configuracoes/pesquisar-operadoras-cartao", {
        params: {
          codigo_publico: titulos[0].codigo_publico,
          tipo_publico: titulos[0].tipo_publico,
        },
      }),
    enabled: activeStep === 2,
  });

  const dataOperadorasCartao: TOperadoraCartao[] = useMemo(
    () => data?.data.data || [],
    [data]
  );

  const { mutate: finalizarRecb, isLoading } = useMutation({
    mutationKey: "registrar-recebimento-titulos",
    mutationFn: (input: IInputRegRecebimento) =>
      axiosClient.post(
        `financeiro/contas-receber/registrar-recebimento-titulos`,
        input
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(`Erro ao registrar recebimento`, { variant: "error" });
    },
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries(
        "table-data-grid-financeiro/contas-receber/pesquisar-titulos-receber"
      );
      enqueueSnackbar(`Recebimento foi realizado com sucesso!`, {
        variant: "success",
      });
      setValue("recebimentoRegistrado", data);
    },
  });
  //

  const handleBack = getValues("funcoesAux.onBack");

  const handleChangeLoja = (value: TObjValueLoja | null) => {
    setValue("finalizacao.loja", value);
  };

  const handleSendRecb = () => {
    const { selecionar_titulos, finalizacao, total_atualizado} = getValues();

    if(finalizacao.aplicarDiferencaComCupom){
    const valores = finalizacao.recebimentos.reduce((acc: number, item: IInternForm) => {
      const valor = formatarMonetárioParaNúmero(item.valor);
      acc += valor;
      return acc;
     }, 0);
  
    const diferenca = Number((total_atualizado - valores).toFixed(2));
    const valorCupom = finalizacao.cupomDesconto.valor;  
    if(diferenca !== valorCupom){
    enqueueSnackbar(`O valor dos recebimentos (${formatarValorMonetario(valores)}) deve conter o desconto de ${formatarValorMonetario(valorCupom)} e deve ser R$ ${formatarValorMonetario(total_atualizado - valorCupom)}`, { variant: "warning" });
    return;
  }
}
    

    const params: IInputRegRecebimento = {
      titulos: selecionar_titulos.map(
        (titulo: IDataListaTitulosReceber) => titulo.id
      ),
      data_recebimento: finalizacao.data_recebimento.format("YYYY-MM-DD"),
      loja_id: finalizacao.loja.id,
      descontar_diferenca: finalizacao.aplicarDiferencaComoDesconto,
      descontar_cupom: finalizacao.aplicarDiferencaComCupom,
      descontar_sem_cupom: finalizacao.aplicarDiferencaSemCupom,
      codigo_cupom_desconto: finalizacao.cupomDesconto.codigo || null,
      adicionar_diferenca: finalizacao.aplicarDiferencaComoJuros,
      gravar_caixa_banco: finalizacao.gravarCaixaBanco,
      valores: finalizacao.recebimentos.map((item: IInternForm) => ({
        valor: formatarMonetárioParaNúmero(item.valor),
        forma_recebimento_id: item.formaPagamento?.id,
        ...(item.pix_pago ? { pix_pago: item.pix_pago } : null),
        ...(item.cartao_parcelas ? { parcelas: item.cartao_parcelas } : null),
        ...(item.cartao_nsu ? { nsu: item.cartao_nsu } : {}),
        ...(item.cartao_autorizacao
          ? { autorizacao: item.cartao_autorizacao }
          : {}),
        ...(item.cheque_banco ? { cod_banco: item.cheque_banco.codigo } : {}),
        ...(item.cheque_agencia ? { agencia: item.cheque_agencia } : {}),
        ...(item.cheque_agencia_digito
          ? { agencia_digito: item.cheque_agencia_digito }
          : {}),
        ...(item.cheque_conta ? { conta: item.cheque_conta } : {}),
        ...(item.cheque_conta_digito
          ? { conta_digito: item.cheque_conta_digito }
          : {}),
        ...(item.cheque_documento
          ? { documento: desformatarCPFCNPJ(item.cheque_documento) }
          : {}),
        ...(item.cheque_num ? { num_cheque: item.cheque_num } : {}),
        ...(item.cheque_seq ? { seq_cheque: item.cheque_seq } : {}),
        ...(item.cheque_data_vencimento
          ? {
            data_vencimento: item.cheque_data_vencimento.format("YYYY-MM-DD"),
          }
          : {}),
        ...(item.cheque_nome_emissor
          ? { nome_emissor: item.cheque_nome_emissor }
          : {}),
        ...(item.cheque_praca ? { praca: item.cheque_praca } : {}),
        ...(item.cheque_conta_contabil
          ? {
            conta_devedora_cod: Number(
              item.cheque_conta_contabil.codigo_contabil
            ),
            conta_devedora_centro_custo: Number(
              item.cheque_conta_contabil.centro_custo || 0
            ),
          }
          : {}),
      })),
    };

    finalizarRecb(params);
  };

  //Valores importantes
  const verificarCaixaBancos = useMemo(() => {
    return dataOperadorasCartao.length > 0 &&
      ignorarGravacaoCaixaBancos
      ? true
      : false;
  }, [dataOperadorasCartao, ignorarGravacaoCaixaBancos]);

  const opcoesRadio = useMemo(
    () => [
      { value: "true", label: "Sim" },
      { value: "false", label: "Não" },
    ],
    []
  );
  //

  const finalizacao = watch("finalizacao");
  const total_atualizado = watch("total_atualizado");

  return (
    <Box maxWidth={600}>
      <Grid container spacing={2}>
        <Grid size={{xs: 12, md: 12}}>
          <Box paddingTop={2}>
            <Typography variant="h6">Registrar recebimento</Typography>
            <Typography variant="body1" color="gray" mb={2}>
              (Você informa manualmente a loja, o valor e a forma de
              recebimento)
            </Typography>

            <SelectLojaComPesquisa
              value={lojaSelect}
              onChange={(value) => handleChangeLoja(value as TLoja)}
              extraParams={{
                somente_vinculadas: Boolean(
                  getValues("finalizacao.usuarioComLoja")
                ),
              }}
            />

            <AlertMarcarDesconto />
            <AplicarCupomDesconto />
            <AlertMarcarJuros />

            {Boolean(lojaSelect) && <Recebimento />}
          </Box>
        </Grid>
        <Grid size={{xs: 12}}>
          {verificarCaixaBancos && Boolean(lojaSelect) && (
            <Controller
              name="finalizacao.gravarCaixaBanco"
              control={control}
              rules={{ required: "Campo obrigatório" }}
              render={({ field }) => (
                <FormControl {...field}>
                  <FormLabel id="gravarCaixaBanco">
                    Gravar caixa banco?
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="gravarCaixaBanco"
                    value={getValues("finalizacao.gravarCaixaBanco")}
                    onChange={(e) =>
                      setValue("finalizacao.gravarCaixaBanco", e.target.checked)
                    }
                  >
                    {opcoesRadio.map((opcao, index) => (
                      <FormControlLabel
                        key={index}
                        value={opcao.value}
                        control={<Radio />}
                        label={opcao.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            />
          )}
        </Grid>
      </Grid>

      <BtnActionWrapper>
        <Button onClick={handleBack} sx={{ pointerEvents: "auto" }}>
          Voltar
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit(handleSendRecb)}
          sx={{ pointerEvents: "auto" }}
          disabled={verificarRegistrarBtn() || isLoading}
        >
          {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
          Registrar
        </Button>
      </BtnActionWrapper>
    </Box>
  );
}